import {KeyboardEvent, useCallback, useRef, useState} from 'react';

import {FocusSection} from '../types/FocusSection';
import {Hour} from '../types/Hour';
import {Minute} from '../types/Minute';
import {
  useTimePickerAutoScroll,
  AutoScrollConfig,
  ScrollOperation,
} from './useTimePickerAutoScroll';

export interface TimePickerKeyboardState {
  focusedSection: FocusSection | null;
  focusedHourIndex: number;
  focusedMinuteIndex: number;
  isKeyboardNavigationActive: boolean;
}

export interface KeyboardNavigationConfig {
  enabled: boolean;
  focusTrapEnabled: boolean;
  initialFocusSection: FocusSection;
  arrowKeyWrapping: boolean;
  autoScrollEnabled?: boolean;
  autoScrollConfig?: Partial<AutoScrollConfig>;
}

export interface FocusState {
  section: FocusSection;
  index: number;
  item: Hour | Minute;
}

export type NavigationAction =
  | {type: 'MOVE_UP'; section: FocusSection}
  | {type: 'MOVE_DOWN'; section: FocusSection}
  | {type: 'CHANGE_SECTION'; targetSection: FocusSection}
  | {type: 'SELECT_ITEM'; section: FocusSection; index: number}
  | {type: 'RESET_FOCUS'};

export interface UseTimePickerKeyboardNavigationProps {
  hours: Hour[];
  minutes: Minute[];
  selectedHour: Hour | null;
  selectedMinute: Minute | null;
  isOpen: boolean;
  onHourSelect: (hour: Hour, focusNext?: boolean) => void;
  onMinuteSelect: (minute: Minute) => void;
  onClose: () => void;
  onOpen: () => void;
  isDisabled?: boolean;
  config?: Partial<KeyboardNavigationConfig>;
  isHourDisabled?: (hour: Hour) => boolean;
  isMinuteDisabled?: (minute: Minute) => boolean;
  dropdownRef?: React.RefObject<HTMLDivElement | null>;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  hourElements?: HTMLElement[];
  minuteElements?: HTMLElement[];
  onScrollStart?: (operation: ScrollOperation) => void;
  onScrollComplete?: (operation: ScrollOperation) => void;
}

export interface UseTimePickerKeyboardNavigationReturn {
  focusState: FocusState | null;
  handleKeyDown: (event: KeyboardEvent) => void;
  handleInputKeyDown: (event: KeyboardEvent) => void;
  resetFocus: () => void;
  moveFocusToSection: (section: FocusSection) => void;
  setFocusedItem: (section: FocusSection, index: number) => void;
  getFocusProps: (
    type: FocusSection,
    index: number
  ) => {
    isFocused: boolean;
    tabIndex: number;
  };
  keyboardState: TimePickerKeyboardState;
  isScrolling: boolean;
  scrollToFocused: (section: FocusSection, index: number) => void;
  cancelScrollAnimation: () => void;
}

const DEFAULT_CONFIG: KeyboardNavigationConfig = {
  enabled: true,
  focusTrapEnabled: true,
  initialFocusSection: 'hours',
  arrowKeyWrapping: true,
  autoScrollEnabled: true,
};

export function useTimePickerKeyboardNavigation(
  props: UseTimePickerKeyboardNavigationProps
): UseTimePickerKeyboardNavigationReturn {
  const config = {...DEFAULT_CONFIG, ...props.config};

  const [keyboardState, setKeyboardState] = useState<TimePickerKeyboardState>({
    focusedSection: null,
    focusedHourIndex: -1,
    focusedMinuteIndex: -1,
    isKeyboardNavigationActive: false,
  });

  const previousFocusElementRef = useRef<HTMLElement | null>(null);

  // Auto-scroll integration
  const autoScroll = useTimePickerAutoScroll({
    focusedSection: keyboardState.focusedSection,
    focusedIndex:
      keyboardState.focusedSection === 'hours'
        ? keyboardState.focusedHourIndex
        : keyboardState.focusedMinuteIndex,
    isDropdownOpen: props.isOpen,
    hourElements: props.hourElements || [],
    minuteElements: props.minuteElements || [],
    config: {
      enabled: config.autoScrollEnabled !== false,
      ...config.autoScrollConfig,
    },
    onScrollStart: props.onScrollStart,
    onScrollComplete: props.onScrollComplete,
  });

  const getValidIndex = useCallback(
    (section: FocusSection, targetIndex: number): number => {
      const items = section === 'hours' ? props.hours : props.minutes;
      const isItemDisabled = section === 'hours' ? props.isHourDisabled : props.isMinuteDisabled;

      let index = targetIndex;

      // Handle wrapping
      if (config.arrowKeyWrapping) {
        if (index < 0) {
          index = items.length - 1;
        } else if (index >= items.length) {
          index = 0;
        }
      } else {
        index = Math.max(0, Math.min(index, items.length - 1));
      }

      // Skip disabled items
      const maxAttempts = items.length;
      let attempts = 0;

      while (attempts < maxAttempts) {
        const item = items[index];
        if (item && !isItemDisabled?.(item as Hour & Minute)) {
          return index;
        }

        // Move to next valid item
        if (
          targetIndex > keyboardState.focusedHourIndex ||
          targetIndex > keyboardState.focusedMinuteIndex
        ) {
          index = (index + 1) % items.length;
        } else {
          index = index - 1 < 0 ? items.length - 1 : index - 1;
        }
        attempts++;
      }

      return targetIndex; // Fallback if no valid item found
    },
    [
      props.hours,
      props.minutes,
      props.isHourDisabled,
      props.isMinuteDisabled,
      config.arrowKeyWrapping,
      keyboardState.focusedHourIndex,
      keyboardState.focusedMinuteIndex,
    ]
  );

  const setFocusedItem = (section: FocusSection, index: number) => {
    if (!config.enabled || props.isDisabled || !props.isOpen) {
      return;
    }

    const validIndex = getValidIndex(section, index);
    setKeyboardState((previousState) => ({
      ...previousState,
      focusedSection: section,
      focusedHourIndex: section === 'hours' ? validIndex : previousState.focusedHourIndex,
      focusedMinuteIndex: section === 'minutes' ? validIndex : previousState.focusedMinuteIndex,
      isKeyboardNavigationActive: true,
    }));

    if (config.autoScrollEnabled !== false) {
      autoScroll.scrollToFocusedItem(section, validIndex);
    }
  };

  const moveFocusToSection = (section: FocusSection) => {
    if (!config.enabled || props.isDisabled) {
      return;
    }

    const items = section === 'hours' ? props.hours : props.minutes;
    const selectedItem = section === 'hours' ? props.selectedHour : props.selectedMinute;

    // Find initial focus index
    let initialIndex = 0;
    if (selectedItem) {
      const selectedIndex = items.indexOf(selectedItem as Hour & Minute);
      if (selectedIndex !== -1) {
        initialIndex = selectedIndex;
      }
    }

    const validIndex = getValidIndex(section, initialIndex);

    setKeyboardState((prev) => ({
      ...prev,
      focusedSection: section,
      focusedHourIndex: section === 'hours' ? validIndex : prev.focusedHourIndex,
      focusedMinuteIndex: section === 'minutes' ? validIndex : prev.focusedMinuteIndex,
      isKeyboardNavigationActive: true,
    }));

    // Trigger auto-scroll after focus change
    if (config.autoScrollEnabled !== false) {
      autoScroll.scrollToFocusedItem(section, validIndex);
    }
  };

  const moveWithinSection = (section: FocusSection, direction: 'up' | 'down') => {
    if (!config.enabled || props.isDisabled || keyboardState.focusedSection !== section) {
      return;
    }

    const currentIndex =
      section === 'hours' ? keyboardState.focusedHourIndex : keyboardState.focusedMinuteIndex;
    const newIndex = direction === 'down' ? currentIndex + 1 : currentIndex - 1;
    const validIndex = getValidIndex(section, newIndex);

    setKeyboardState((prev) => ({
      ...prev,
      focusedHourIndex: section === 'hours' ? validIndex : prev.focusedHourIndex,
      focusedMinuteIndex: section === 'minutes' ? validIndex : prev.focusedMinuteIndex,
    }));

    // Trigger auto-scroll after focus change
    if (config.autoScrollEnabled !== false) {
      autoScroll.scrollToFocusedItem(section, validIndex);
    }
  };

  const selectCurrentItem = () => {
    if (!keyboardState.isKeyboardNavigationActive || !keyboardState.focusedSection) {
      return;
    }

    const {focusedSection} = keyboardState;
    const index =
      focusedSection === 'hours'
        ? keyboardState.focusedHourIndex
        : keyboardState.focusedMinuteIndex;
    const items = focusedSection === 'hours' ? props.hours : props.minutes;
    const item = items[index];

    if (!item) {
      return;
    }

    if (focusedSection === 'hours') {
      props.onHourSelect(item as Hour, true);
      // Auto-move to minutes section after selecting hour
      setTimeout(() => {
        moveFocusToSection('minutes');
      }, 0);
    } else {
      props.onMinuteSelect(item as Minute);
      // Close dropdown after selecting minute
      setTimeout(() => {
        props.onClose();
      }, 0);
    }
  };

  const resetFocus = useCallback(() => {
    setKeyboardState({
      focusedSection: null,
      focusedHourIndex: -1,
      focusedMinuteIndex: -1,
      isKeyboardNavigationActive: false,
    });
  }, []);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!config.enabled || props.isDisabled || !props.isOpen) {
      return;
    }

    const {key} = event;

    switch (key) {
      case 'ArrowUp':
        event.preventDefault();
        if (keyboardState.focusedSection) {
          moveWithinSection(keyboardState.focusedSection, 'up');
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (keyboardState.focusedSection) {
          moveWithinSection(keyboardState.focusedSection, 'down');
        }
        break;

      case 'ArrowLeft':
        event.preventDefault();
        if (keyboardState.focusedSection === 'minutes') {
          moveFocusToSection('hours');
        }
        break;

      case 'ArrowRight':
      case 'Tab':
        event.preventDefault();
        if (keyboardState.focusedSection === 'hours') {
          moveFocusToSection('minutes');
        } else if (key === 'Tab' && keyboardState.focusedSection === 'minutes') {
          // Cycle back to hours on Tab from minutes
          moveFocusToSection('hours');
        }
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        selectCurrentItem();
        break;

      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        resetFocus();
        props.onClose();
        break;

      default:
        // Let other keys bubble up
        break;
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent) => {
    if (!config.enabled || props.isDisabled) {
      return;
    }

    const {key} = event;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (!props.isOpen) {
        // Store current focus element
        previousFocusElementRef.current = document.activeElement as HTMLElement;
        // Open dropdown
        props.onOpen();
        // Focus initial section after dropdown opens
        setTimeout(() => {
          // Force blur the input and any active element
          if (props.inputRef?.current) {
            props.inputRef.current.blur();
          }
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }

          // Small delay to ensure blur completes
          setTimeout(() => {
            if (props.dropdownRef?.current) {
              props.dropdownRef.current.focus();
            }
            moveFocusToSection(config.initialFocusSection);
          }, 10);
        }, 100);
      }
    }
  };

  const getFocusProps = useCallback(
    (type: FocusSection, index: number) => {
      const isFocused =
        keyboardState.isKeyboardNavigationActive &&
        keyboardState.focusedSection === type &&
        (type === 'hours' ? keyboardState.focusedHourIndex : keyboardState.focusedMinuteIndex) ===
          index;

      return {
        isFocused,
        tabIndex: isFocused ? 0 : -1,
      };
    },
    [keyboardState]
  );

  const getCurrentFocusState = useCallback((): FocusState | null => {
    if (!keyboardState.isKeyboardNavigationActive || !keyboardState.focusedSection) {
      return null;
    }

    const section = keyboardState.focusedSection;
    const index =
      section === 'hours' ? keyboardState.focusedHourIndex : keyboardState.focusedMinuteIndex;
    const items = section === 'hours' ? props.hours : props.minutes;
    const item = items[index];

    if (!item) {
      return null;
    }

    return {
      section,
      index,
      item,
    };
  }, [keyboardState, props.hours, props.minutes]);

  return {
    focusState: getCurrentFocusState(),
    handleKeyDown,
    handleInputKeyDown,
    resetFocus,
    moveFocusToSection,
    setFocusedItem,
    getFocusProps,
    keyboardState,
    isScrolling: autoScroll.isScrolling,
    scrollToFocused: autoScroll.scrollToFocusedItem,
    cancelScrollAnimation: autoScroll.cancelScrollAnimation,
  };
}
